import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {DownloadedIndoorWorkout} from '../../../Models/DownloadedIndoorWorkout';
import {Graphs} from '../../../Graphs/IndoorsGraphs';
import {StrokeAnalyzer} from '../../Components/Workout/Indoor/StrokeAnalyzer/StrokeAnalyzer';
import {IndoorFullWorkout} from '../../../Models/FullWorkout';
import {IndoorIntervalTable} from '../../Components/Workout/Indoor/IndoorIntervalTable';
import {NetworkResult} from '../../../Network/NetworkResult';
import {NetworkRequestStatusView} from '../../Components/Utils/NetworkRequestStatusView';
import {Loader} from '../../Components/Utils/Loader';
import {SplitTable} from '../../Components/Workout/Indoor/SplitTable';
import {FullWorkoutSummary} from '../../Components/Workout/FullWorkoutSummary';
import {Me} from '../../../Models/Me';
import {MachineConfigurationBadges} from '../../Components/Workout/Indoor/MachineConfigurationBadges';
import {Link} from 'react-router-dom';
import {Sidebar} from './IndoorComponents/Sidebar';
import {EditTitleForm} from '../../Components/Workout/Generic/EditTitleForm';
import {HighlightedRangeSummary} from './IndoorComponents/HighlightedRangeSummary';
import {downloadedIndoorWorkoutToStrokes} from '../../../Strokes/downloadedIndoorWorkoutToStrokes';
import {FeatureFlags} from '../../../FeatureFlags';
import {PowerModeDropdown} from '../../Components/Utils/PowerModeDropdown';
import {useSettings} from '../../Settings/settings';

import {RangeInput} from '../../../Models/RangeInput';

interface OwnProps {
    title: string;
    me: Me;
    indoorFullWorkout: IndoorFullWorkout;
    downloadedIndoorWorkoutResult: NetworkResult<DownloadedIndoorWorkout>;
}

function hasStrokes(downloadedIndoorWorkout: DownloadedIndoorWorkout): boolean {
    return downloadedIndoorWorkout.workoutIntervals.some((workoutInterval) => {
        return workoutInterval.strokes.length > 0;
    });
}

export const IndoorFull: FunctionComponent<OwnProps> = ({
    title,
    me,
    indoorFullWorkout,
    downloadedIndoorWorkoutResult,
}) => {
    const [highlightedRange, setHighlightedRange] = useState<RangeInput | null>(null);
    const {powerMode, setPowerMode} = useSettings();

    return (
        <>
            <div className="container">
                <div className="row">
                    <div className="col-12 d-flex flex-row justify-content-start">
                        <Link to="/workouts" className="btn btn-back-arrow">
                            <i className="bi bi-arrow-left"></i>
                        </Link>
                        {me.id === indoorFullWorkout.user_id && (
                            <EditTitleForm id={indoorFullWorkout.id} title={title} />
                        )}
                        {me.id !== indoorFullWorkout.user_id && (
                            <h1>
                                {title} by {indoorFullWorkout.public_user.display_name || 'unnamed'}
                            </h1>
                        )}
                    </div>
                    <div className="col-12 d-flex flex-row">
                        <MachineConfigurationBadges
                            className="mb-3 p-2 me-auto"
                            machineConfigurations={indoorFullWorkout.machine_configurations}
                        />
                        {FeatureFlags.powerSplit && (
                            <PowerModeDropdown powerMode={powerMode} setPowerMode={setPowerMode} />
                        )}
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-lg-9">
                        <FullWorkoutSummary fullWorkout={indoorFullWorkout} />
                        <IndoorIntervalTable
                            className="mt-3"
                            highlightedRange={highlightedRange}
                            onRangeChange={setHighlightedRange}
                            indoorFullWorkout={indoorFullWorkout as IndoorFullWorkout}
                        />
                        <NetworkRequestStatusView
                            result={downloadedIndoorWorkoutResult}
                            loader={() => <Loader className="mt-3" />}
                            error={() => null}
                            component={(downloadedIndoorWorkout) =>
                                hasStrokes(downloadedIndoorWorkout) && (
                                    <>
                                        <Graphs
                                            className="mt-3"
                                            range={highlightedRange}
                                            onRangeChange={setHighlightedRange}
                                            downloadedIndoorWorkout={downloadedIndoorWorkout}
                                        />
                                        <SplitTable
                                            className="mt-3"
                                            highlightedRange={highlightedRange}
                                            downloadedIndoorWorkout={downloadedIndoorWorkout}
                                            onRangeChange={setHighlightedRange}
                                        />
                                        <StrokeAnalyzer
                                            className="mt-3"
                                            range={highlightedRange}
                                            onRangeChange={setHighlightedRange}
                                            downloadedIndoorWorkout={downloadedIndoorWorkout}
                                        />
                                    </>
                                )
                            }
                        />
                    </div>
                    <div className="col-12 col-lg-3">
                        <NetworkRequestStatusView
                            result={downloadedIndoorWorkoutResult}
                            loader={() => null}
                            error={() => null}
                            component={(downloadedIndoorWorkout) => {
                                if (!highlightedRange) {
                                    return (
                                        <Sidebar
                                            indoorFullWorkout={indoorFullWorkout} 
                                            me={me}
                                            strokes={downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout)}
                                        />
                                    ) ;
                                }
                                return (
                                    <HighlightedRangeSummary
                                        workoutId={indoorFullWorkout.id}
                                        workoutUserId={indoorFullWorkout.user_id}
                                        strokes={downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout)}
                                        range={highlightedRange}
                                        onRangeChange={setHighlightedRange}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
